import * as React from 'react';

import {
  TechDataFootnoteLabel,
  DangerousElement,
  FootnoteReference,
  isAvailableSoonVehicle,
  useAvailableSoonLabels,
  VehicleBasic,
  VehicleDetail,
  useServicesContext,
  useDescription,
} from '@oneaudi/vtp-shared';
import { Text } from '@audi/audi-ui-react-v2';

import { CommonHighlight, HighlightWrap } from './CommonStyledComponents';
import { HighLightItem } from '../interface/highlights.interfaces';
import HighlightInfo from './HighlightInfo';

interface SimpleItemProps {
  item: HighLightItem;
  vehicle: VehicleBasic;
  vehicleDetail?: VehicleDetail;
}

const SimpleItem: React.FC<SimpleItemProps> = ({ item, vehicle, vehicleDetail }) => {
  const { IconLabel, InfoLink, TechdataKey } = item;
  const [labelText, descriptionText] = useDescription(
    IconLabel || '',
    TechdataKey,
    vehicle,
    vehicleDetail,
  );
  const { getConfiguration } = useServicesContext();
  const { scopes } = getConfiguration()!;
  const showAvailableFromDateIndependentlyFromData = scopes?.showAvailableSoonDate;

  const availableSoonVehicle = isAvailableSoonVehicle(
    vehicle,
    showAvailableFromDateIndependentlyFromData,
  );

  // show fn_available_soon & fn_expected_mileage_stockcar only for dealer 03611
  const hasSpecialDealerFootnote =
    vehicle?.availableFromCode === 'date' && vehicle?.dealer?.id === '03611';

  const isAvailableFromCodeSoon = vehicle?.availableFromCode === 'soon';
  const isAvailableFromCodeNow = vehicle?.availableFromCode === 'now';
  const isAvailableFromCodeDate = vehicle?.availableFromCode === 'date';

  const { availableFromDateOrLabel, expectedMileage } = useAvailableSoonLabels(vehicle);
  const isAvailableFromTechDataKey = TechdataKey.includes('AvailableFromDate');
  const isMileageTechDataKey = TechdataKey.includes('Mileage');
  const labelTextValue =
    // eslint-disable-next-line no-nested-ternary
    (availableSoonVehicle ? (isMileageTechDataKey ? expectedMileage : labelText) : labelText) || '';

  const carType = vehicle.type === 'N' ? 'new' : 'used';

  return (
    <>
      {descriptionText ? (
        <HighlightWrap className="sc-detail-highlight-wrapper" data-tech-key={TechdataKey}>
          <CommonHighlight className="sc-detail-highlight" data-tech-key={TechdataKey}>
            <Text as="p" variant="copy2" spaceStackEnd="xxs" className="sc-detail-highlight-label">
              <DangerousElement html={labelTextValue} sanitize={false} as="span" />
              {hasSpecialDealerFootnote && TechdataKey.includes('AvailableFromDate') && (
                <>
                  <FootnoteReference refId={`fn_available_soon_stockcar_${carType}`} />
                </>
              )}
              {hasSpecialDealerFootnote && isMileageTechDataKey && (
                <FootnoteReference refId="fn_expected_mileage_stockcar" />
              )}
              {!hasSpecialDealerFootnote && TechdataKey === 'Mileage' && (
                <FootnoteReference refId="fn_miles_stockcar" />
              )}
              {TechdataKey === 'ElectricRange' && (
                <>
                  <FootnoteReference refId="fn_wltp_electric_range_stockcar" />
                  <FootnoteReference refId={`fn_consumption_stockcar_${carType}`} />
                </>
              )}

              {isAvailableFromCodeSoon && TechdataKey.includes('AvailableFromDate') && (
                <>
                  <FootnoteReference refId={`fn_available_soon_stockcar_${carType}`} />
                </>
              )}

              {isAvailableFromCodeNow && TechdataKey.includes('AvailableFromDate') && (
                <>
                  <FootnoteReference refId="fn_available_now" />
                  <FootnoteReference refId={`fn_available_now_stockcar_${carType}`} />
                </>
              )}

              {isAvailableFromCodeDate && TechdataKey.includes('AvailableFromDate') && (
                <>
                  <FootnoteReference refId="fn_available_date" />
                  <FootnoteReference refId={`fn_available_date_stockcar_${carType}`} />
                </>
              )}
            </Text>
            <Text as="span" variant="copy1" weight="bold">
              <TechDataFootnoteLabel
                vehicle={vehicle}
                techDataKey={TechdataKey}
                techDataValue={
                  availableSoonVehicle && isAvailableFromTechDataKey
                    ? availableFromDateOrLabel
                    : descriptionText
                }
              />
            </Text>
            {InfoLink ? <HighlightInfo infoLink={InfoLink} /> : ''}
          </CommonHighlight>
        </HighlightWrap>
      ) : null}
    </>
  );
};

export default SimpleItem;
